import { v4 as uuid } from 'uuid';
import message from '@meitu/saas-ui/es/message';
import {
    blob2File,
    getWebmInfoAsync,
    IMAGE_TYPE,
    isWebm as isWebmFn,
    loadImageAsync,
    svg2Png,
    uploadVectorFile,
} from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { parseVector } from '@meitu/saas-editor-core/es/utils/vector-kit';
import { saveSvgParams } from '@meitu/saas-editor-ui/es/components/image-upload';
import { EVENT_FILES_UPLOAD, events } from '@/events-bus';
// import { IMAGE_TYPE } from '@/constants';
import { editor } from '@/instance';
import { saveMyPicture } from '@/services/record';
import { MAX_IMAGE_FILE_SIZE } from '@/types';
import { getMediaType } from '@/utils/layer';

interface IFileResult {
    url: string;
    id: string;
    name: string;
    source?: string;
    width: number;
    height: number;
    blobUrl: any;
    svgInfo: string;
    mediaLayerInfo: string;
}
const sleep = (timeout = 1000) =>
    new Promise((resolve, reject) => {
        setTimeout(resolve, timeout);
    });
const useFilesUpload = () => {
    return {
        filesUpload: async (files: File[], source = '') => {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise<IFileResult[]>(async (resolve, reject) => {
                const resultList: IFileResult[] = [];

                editor.services.mask.show({
                    loading: true,
                });
                for (let i = 0; i < files.length; i++) {
                    let file = files[i] as any;

                    if (file.size > MAX_IMAGE_FILE_SIZE) {
                        message.warning('图片最大只支持20M');
                        editor.services.mask.hide();
                        reject();
                    }

                    try {
                        const name = file?.name?.lastIndexOf('.') || 0;
                        const id = uuid();
                        const type = file?.name?.substring(name)?.toLowerCase() || '';
                        let svgInfo: saveSvgParams;

                        if (file && (IMAGE_TYPE.includes(file.type) || IMAGE_TYPE.includes(type))) {
                            if (file.type === 'image/heic') {
                                try {
                                    const heic2any = (await import(/* webpackChunkName: "heic2any" */ 'heic2any'))
                                        .default as any;
                                    const resultBlob = (await heic2any({
                                        blob: file, // 将heic转换成一个buffer数组的图片
                                        toType: 'image/jpg', //要转化成具体的图片格式，可以是png/gif
                                        quality: 1,
                                    })) as Blob;
                                    file = await blob2File(resultBlob);
                                } catch (error) {
                                    message.error('HEIC图片解析失败，建议您转换为其他图片格式上传');
                                }
                            }
                            if (file.type === 'image/svg+xml') {
                                try {
                                    const { pngFile, svgEl } = await svg2Png(file);
                                    const fileObs = await uploadFileToObs(file);
                                    const { text, info } = await parseVector(svgEl);
                                    const imageUrl = await uploadVectorFile(text);
                                    svgInfo = {
                                        image: imageUrl.url,
                                        info,
                                        file: fileObs.url,
                                    };
                                    file = pngFile;
                                } catch (error) {
                                    console.error('转换失败', error);
                                }
                            }
                            const isWebm = isWebmFn(file);

                            const blobUrl = (file && window.URL.createObjectURL(file)) || '';

                            // 获取视频webm宽高
                            const loadInfo = isWebm ? getWebmInfoAsync(blobUrl) : loadImageAsync(blobUrl, false);

                            let width = 0;
                            let height = 0;

                            try {
                                const info: any = await loadInfo;
                                width = isWebm ? info.videoWidth : info.width;
                                height = isWebm ? info.videoHeight : info.height;
                            } catch (error) {
                                console.error('Failed to load media info:', error);
                                throw error;
                            }

                            const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';

                            uploadFileToObs(file, undefined, fileExtension)
                                .then(async (data: any) => {
                                    const { url } = data;

                                    const { isMediaLayer, mediaType } = getMediaType(url);

                                    resultList.push({
                                        url,
                                        id,
                                        name: (file?.name as string) || '',
                                        source,
                                        width,
                                        height,
                                        blobUrl,
                                        svgInfo: JSON.stringify(svgInfo),
                                        mediaLayerInfo: isMediaLayer
                                            ? JSON.stringify({
                                                  isMediaLayer,
                                                  mediaType,
                                              })
                                            : '',
                                    });
                                    if (resultList.length === files.length) {
                                        resolve(resultList);
                                    }
                                })
                                .catch((error: any) => {
                                    resultList.push({
                                        url: '',
                                        id: '',
                                        name: '',
                                        source: '',
                                        width: 0,
                                        height: 0,
                                        blobUrl: '',
                                        svgInfo: '',
                                        mediaLayerInfo: '',
                                    });
                                    if (resultList.length === files.length) {
                                        resolve(resultList);
                                    }
                                });
                        } else if (file) {
                            editor.services.mask.hide();
                            reject();
                            message.warning('暂时只支持JPG/JPEG/PNG/HEIC/SVG格式图片');
                        }
                    } catch (error) {
                        editor.services.mask.hide();
                        reject();
                        message.error('图片解析失败，建议您转换为其他图片格式上传');
                    }
                }
            });
        },
    };
};

export const saveMyPictureByUrl = async (file: IFileResult) => {
    const { url, id, name, svgInfo, blobUrl, mediaLayerInfo } = file;

    try {
        // 局部数据，不存放store
        events.emit(EVENT_FILES_UPLOAD, {
            type: 'pending',
            blobUrl,
            id,
            name: name,
            ext: mediaLayerInfo || svgInfo,
        });
        await sleep(100);

        const isWebm = isWebmFn(url);

        const loadInfo = isWebm ? getWebmInfoAsync(blobUrl) : loadImageAsync(blobUrl, false);

        let width = 0;
        let height = 0;
        try {
            const info: any = await loadInfo;
            width = isWebm ? info.videoWidth : info.width;
            height = isWebm ? info.videoHeight : info.height;
        } catch (error) {
            console.error('Failed to load media info:', error);
            throw error;
        }

        if (width === 0 || height === 0) {
            return;
        }

        const {
            data: {
                response: { id: _id },
            },
        } = await saveMyPicture({
            url,
            width: width || 0,
            height: height || 0,
            ext: mediaLayerInfo || svgInfo,
        });
        events.emit(EVENT_FILES_UPLOAD, {
            type: 'success',
            url,
            id,
            _id: _id.toString(),
            width: width,
            height: height,
            ext: mediaLayerInfo || svgInfo,
        });
        // 存入 store 层数据 （目前没有在服务端存储）
        // setMaterialImages(list);
        message.success('添加成功');
    } catch (error) {
        events.emit(EVENT_FILES_UPLOAD, {
            type: 'fail',
            file,
            id,
        });
    }
};

export default useFilesUpload;
